import React, { useEffect, useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTheme } from "emotion-theming";
import {
  ListenMessage,
  LISTEN_MESSAGE_TYPE,
  PostMessage,
  POST_MESSAGE_TYPE,
} from "services/MessageService";
import { ApplicationContext } from "wrapper/ApplicationWrapper/context";
import { PaymentContext } from "wrapper/PaymentWrapper/context/paymentContext";
import { get } from "../../logics/src/utils/helpers";
import { doRedirect } from "helpers/utils/redirection";
import { CLIENT_URLS } from "constants/urls";
import { DEVICE_TYPE } from "constants/index";
import { OtpPciContainer, OtpPciDesktopLayout } from "PCI/assets/style";
import { PCI_IFRAME_IDS } from "PCI/utills";

import Consent from "@nykaa/payment-sdk/src/Consent/mobile";
import { ExpressCheckoutContext } from "wrapper/ExpressCheckoutWrapper/context/expressCheckoutContext";
import { PAYMENT_FLOWS } from "constants/payment";
import { getIcons, ICONS_NAMES } from "helpers/utils/getIcons";
import { getPlatform } from "../../helpers/index";
import { pushEvent } from "helpers/utils/checkoutDataLayer";
import { logger } from "logging";

export const OtpPci = ({ pciPageLoadData, setLoading }: any) => {
  const theme: Theme = useTheme();
  const history = useHistory();
  const platform = getPlatform();
  const paymentContext = useContext(PaymentContext);
  const expressContext = useContext(ExpressCheckoutContext);
  const { applicationData } = useContext(ApplicationContext);
  let pciOtpInfo = get(paymentContext, "pciOtpInfo");
  let setPciOtpScreenVisible =
    get(paymentContext, "setPciOtpScreenVisible") || get(expressContext, "setShowOtpScreen");
  let setPciOtpScreenVisibleForSaved =
    get(paymentContext, "setPciOtpScreenVisibleForSaved") ||
    get(expressContext, "setShowOtpScreen");
  let setLoaderMessage = get(paymentContext, "setLoaderMessage");
  const flow = pciPageLoadData?.applicationData?.flow;
  const { deviceType } = applicationData;
  const [showConsentSheet, setShowConsentSheet] = useState(false);
  const ConsentSheetConfig = {
    submitConsentSubTitle: "You're nearly done! Enter the OTP and place your order now.",
    submitConsentTitle: "Are you leaving?",
  };

  if (flow === PAYMENT_FLOWS.EXPRESS_CHECKOUT) {
    pciOtpInfo = get(expressContext, "pciOtpInfo");
    setPciOtpScreenVisible = get(expressContext, "setShowOtpScreen");
    setPciOtpScreenVisibleForSaved = get(expressContext, "setShowOtpScreen");
    setLoaderMessage = get(expressContext, "setLoaderMessage");
  }

  const consentOnActionButtonClick = () => {
    setShowConsentSheet(false);
  };

  const consentOnDismissActionButtonClick = () => {
    setLoading(true);
    setShowConsentSheet(false);
    doRedirect({
      url: `${CLIENT_URLS.POST_PAYMENT_PAGE}?orderId=${pciOtpInfo?.orderId}`,
      isClientRedirection: false,
    });
    return;
  };

  const consentActionButtonText = "Continue with payment";
  const consentDismissButtonText = "Go back";

  /**
   * This block handles if user try to directly
   * land on otp page of refresh the otp page
   */
  if (!pciOtpInfo?.token) {
    window.history.back();
    return null;
  }

  useEffect(() => {
    ListenMessage(LISTEN_MESSAGE_TYPE.INITIATE_LOAD_OTP, res => {
      PostMessage(
        {
          messageEventName: POST_MESSAGE_TYPE.LOAD,
          data: { ...pciPageLoadData, otpData: pciOtpInfo },
        },
        PCI_IFRAME_IDS.OTP
      );
    });

    ListenMessage(LISTEN_MESSAGE_TYPE.CLOSE_OTP_SCREEN, res => {
      if (deviceType === DEVICE_TYPE.MOBILE) {
        setShowConsentSheet(true);
      } else {
        setLoading(true);
        setPciOtpScreenVisible(false);
        setPciOtpScreenVisibleForSaved(false);
        doRedirect({
          url: `${CLIENT_URLS.POST_PAYMENT_PAGE}?orderId=${pciOtpInfo?.orderId}`,
          isClientRedirection: false,
        });
      }
    });

    ListenMessage(LISTEN_MESSAGE_TYPE.PAYMENT_STATUS_SUCCESS, res => {
      /** this state is for stoping the loaader and redirecting to paymentSuccessPage*/
      setLoading(false);
      doRedirect({
        url: `${CLIENT_URLS.POST_PAYMENT_PAGE}?orderId=${res?.orderId}`,
        isClientRedirection: false,
      });
    });

    ListenMessage(LISTEN_MESSAGE_TYPE.PAYMENT_STATUS_FAIL, res => {
      // redirectToPaymentPreConfirmationPage(history, `orderId=${res?.orderId}`)
      setLoading(false);
      doRedirect({
        url: `${CLIENT_URLS.POST_PAYMENT_PAGE}?orderId=${res?.orderId}`,
        isClientRedirection: false,
      });
    });

    /** for up the starting and ending loading state of loader */
    ListenMessage(LISTEN_MESSAGE_TYPE.OTP_START_LOADER, res => {
      setLoading(true);
      if (res?.loaderMessage) {
        setLoaderMessage(res?.loaderMessage);
      }
    });

    ListenMessage(LISTEN_MESSAGE_TYPE.OTP_END_LOADER, res => {
      setLoading(false);
      setLoaderMessage("");
    });

    ListenMessage(LISTEN_MESSAGE_TYPE.DO_POST_TRACK_ACTION, res => {
      try {
        const {
          event,
          data
        } = res;
        pushEvent(event, data);
      } catch (error) {
        logger.error('Error in DO_POST_TRACK_ACTION', error);
      }
    });
  }, []);

  /**
   * NOTE: This code is special as it solves all back button issues
   * NOTE: Don't remove this code
   * This code provides the functionality to do some action before going back to previous navigation url or browser history url.
   * Here: On click of browser back button , it should work the as same as work in application header back button.
   */

  useEffect(() => {
    const unblock = history.block((_location, action) => {
      if (action == "POP") {
        setShowConsentSheet(true); // bottomsheet will open when user has done changes and clicks broswer back button
        return false;
      }
      return;
    });

    return () => {
      //unmount condition
      unblock();
    };
  }, [history]); // Must be blank to work in component unmount condtion

  const common = () => (
    <Consent
      bottomSheetVisible={showConsentSheet}
      bottomSheetClose={consentOnActionButtonClick}
      consentInfo={ConsentSheetConfig}
      bottomSheetIcon={getIcons(platform, ICONS_NAMES.CLEAR)}
      consentActionButtonText={consentActionButtonText}
      consentDismissButtonText={consentDismissButtonText}
      consentOnActionButtonClick={consentOnActionButtonClick}
      consentOnDismissActionButtonClick={consentOnDismissActionButtonClick}
      theme={theme}
    />
  );

  const desktop = () => {
    return (
      <OtpPciDesktopLayout>
        <OtpPciContainer
          id={PCI_IFRAME_IDS.OTP}
          sandbox="allow-scripts allow-forms allow-top-navigation allow-same-origin allow-orientation-lock allow-pointer-lock"
          src={`${process.env.PCI_ORIGIN}/otp`}
        />
        {common()}
      </OtpPciDesktopLayout>
    );
  };

  const msite = () => {
    return (
      <>
        <OtpPciContainer
          id={PCI_IFRAME_IDS.OTP}
          sandbox="allow-scripts allow-forms allow-top-navigation allow-same-origin allow-orientation-lock allow-pointer-locks"
          src={`${process.env.PCI_ORIGIN}/otp`}
        />
        {common()}
      </>
    );
  };

  if (pciOtpInfo) {
    if (deviceType === DEVICE_TYPE.DESKTOP) {
      return desktop();
    } else {
      return msite();
    }
  }
  return null;
};
